import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

/**
 * DuplicateReviewModal - Side-by-side comparison of potential duplicates
 * 
 * Shows all suspected duplicates for a given seed record.
 * User can:
 * 1. Pick which one to keep as the "canonical" (main) record
 * 2. Confirm others as duplicates one-by-one or all at once
 * 3. Mark individual records as "not a duplicate"
 * 
 * PBFS Color Scheme:
 * - Green: #0DB14B (primary action)
 * - Navy: #00314F (backgrounds)
 * - Blue: #235EAC (secondary)
 * - Gray: #939598 (disabled)
 */

const PBFS = {
  green: '#0DB14B',
  navy: '#00314F',
  blue: '#235EAC',
  gray: '#939598'
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function DuplicateReviewModal({
  isOpen,
  onClose,
  seedRecord,
  duplicateGroup = [],
  reason = '',
  backendUrl,
  onConfirmed,
  onMarkedNotDuplicate
}) {
  // State for workflow
  const [selectedCanonical, setSelectedCanonical] = useState(null);
  const [confirmedDuplicates, setConfirmedDuplicates] = useState(new Set());
  const [notDuplicates, setNotDuplicates] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState('select'); // 'select' | 'confirm' | 'done'
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);
  const [submitProgress, setSubmitProgress] = useState(''); // 🆕 Progress tracking
  const [jobElapsedTime, setJobElapsedTime] = useState(0); // 🆕 Timer display
  const [lightboxImage, setLightboxImage] = useState(null); // 🆕 Image lightbox

  // Reset state when modal opens with new data
  useEffect(() => {
    if (isOpen && seedRecord) {
      setSelectedCanonical(seedRecord._id || seedRecord.id);
      setConfirmedDuplicates(new Set());
      setNotDuplicates(new Set());
      setStep('select');
      setCurrentIndex(0);
      setError(null);
    }
  }, [isOpen, seedRecord]);

  // All records including seed
  const allRecords = useMemo(() => {
    if (!seedRecord) return [];
    const seedId = seedRecord._id || seedRecord.id;
    const others = (duplicateGroup || []).filter(r => (r._id || r.id) !== seedId);
    return [seedRecord, ...others];
  }, [seedRecord, duplicateGroup]);

  // Records that aren't marked as "not duplicate"
  const remainingRecords = useMemo(() => {
    return allRecords.filter(r => !notDuplicates.has(r._id || r.id));
  }, [allRecords, notDuplicates]);

  // Records to confirm (excluding canonical and not-duplicates)
  const toConfirm = useMemo(() => {
    return remainingRecords.filter(r => 
      (r._id || r.id) !== selectedCanonical && 
      !notDuplicates.has(r._id || r.id)
    );
  }, [remainingRecords, selectedCanonical, notDuplicates]);

  const handleSelectCanonical = (record) => {
    setSelectedCanonical(record._id || record.id);
  };

  const handleMarkNotDuplicate = async (record) => {
    const recordId = record._id || record.id;
    setNotDuplicates(prev => new Set([...prev, recordId]));
    
    // Also call backend to persist
    try {
      const token = localStorage.getItem('token');
      await fetch(`${backendUrl}/api/admin/duplicates/not-duplicate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ inspection_id: recordId, note: 'Marked from duplicate review modal' })
      });
      if (onMarkedNotDuplicate) onMarkedNotDuplicate(recordId);
    } catch (err) {
      console.error('Failed to mark not-duplicate:', err);
    }
  };

  const handleStartConfirmation = () => {
    if (!selectedCanonical) {
      setError('Please select which record to keep as the main record');
      return;
    }
    if (toConfirm.length === 0) {
      setError('No duplicates to confirm');
      return;
    }
    setStep('confirm');
    setCurrentIndex(0);
    setError(null);
  };

  const handleConfirmOne = () => {
    const currentRecord = toConfirm[currentIndex];
    if (currentRecord) {
      setConfirmedDuplicates(prev => new Set([...prev, currentRecord._id || currentRecord.id]));
    }
    
    if (currentIndex < toConfirm.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Done with all confirmations
      handleFinalSubmit();
    }
  };

  const handleSkipOne = () => {
    // Skip means mark as not-duplicate
    const currentRecord = toConfirm[currentIndex];
    if (currentRecord) {
      handleMarkNotDuplicate(currentRecord);
    }
    
    if (currentIndex < toConfirm.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Done
      handleFinalSubmit();
    }
  };

  const handleConfirmAll = async () => {
    // Confirm all remaining as duplicates
    const allIds = new Set(toConfirm.map(r => r._id || r.id));
    setConfirmedDuplicates(allIds);
    await handleFinalSubmit(allIds);
  };

  const handleFinalSubmit = async (confirmedSet = confirmedDuplicates) => {
    if (!selectedCanonical) return;
    
    const memberIds = [
      selectedCanonical,
      ...Array.from(confirmedSet)
    ].filter(Boolean);

    if (memberIds.length < 2) {
      // Nothing to confirm
      setStep('done');
      onClose();
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSubmitProgress('Submitting duplicate confirmation job...');
    setJobElapsedTime(0);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/admin/duplicates/confirm-async`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          canonical_inspection_id: selectedCanonical,
          member_inspection_ids: memberIds,
          reason: reason || 'Confirmed via Duplicate Review Modal',
          confidence: 0.85,
          requested_by: 'admin'
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.detail || 'Failed to confirm duplicates');
      }

      // Poll for job completion with extended timeout and better logging
      const jobId = data.job_id;
      let job = { status: 'queued' };
      const startedAt = Date.now();
      
      console.log(`🔄 Polling for job ${jobId} completion...`);
      setSubmitProgress(`Job ${jobId} queued. Waiting for worker...`);
      
      // Timer to update elapsed time display
      const timerInterval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAt) / 1000);
        setJobElapsedTime(elapsed);
      }, 1000);
      
      while (job.status === 'queued' || job.status === 'running') {
        const elapsed = Date.now() - startedAt;
        
        // Extended timeout: 180 seconds (3 minutes) for large duplicate groups
        if (elapsed > 180000) {
          clearInterval(timerInterval);
          throw new Error(`Job timed out after ${Math.floor(elapsed/1000)}s. The duplicate jobs worker may be overloaded. Try confirming fewer duplicates at once.`);
        }
        
        // Update progress message
        if (job.status === 'running') {
          const progress = job.progress || {};
          setSubmitProgress(`Processing: ${progress.updated || 0} / ${progress.total || memberIds.length} submissions updated...`);
        } else {
          setSubmitProgress(`Job queued. Waiting for worker... (${Math.floor(elapsed/1000)}s)`);
        }
        
        // Log progress every 10 seconds
        if (elapsed % 10000 < 1000) {
          console.log(`⏳ Job ${jobId} still ${job.status}... (${Math.floor(elapsed/1000)}s elapsed)`);
        }
        
        await new Promise(r => setTimeout(r, 1000)); // Poll every 1 second
        
        const pollToken = localStorage.getItem('token');
        const pollRes = await fetch(`${backendUrl}/api/admin/duplicates/jobs/${encodeURIComponent(jobId)}`, {
          headers: { 
            'X-Local-Dev': 'true',
            ...(pollToken && { 'Authorization': `Bearer ${pollToken}` })
          }
        });
        
        if (!pollRes.ok) {
          console.warn(`⚠️ Job poll failed with status ${pollRes.status}`);
          continue; // Keep trying
        }
        
        const pollData = await pollRes.json().catch(() => ({}));
        job = pollData.job || { status: 'failed' };
        
        console.log(`📊 Job status: ${job.status}`, job.progress || '');
      }

      clearInterval(timerInterval);
      console.log(`✅ Job ${jobId} completed:`, job.status);
      setSubmitProgress('Completed! Updating UI...');

      if (job.status !== 'completed') {
        throw new Error(job.last_error || 'Job failed');
      }

      setStep('done');
      if (onConfirmed) {
        onConfirmed({
          canonicalId: selectedCanonical,
          duplicateIds: Array.from(confirmedSet),
          groupId: job.result?.group_id || data.group_id
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 pt-20 pb-6 bg-black/70 backdrop-blur-sm">
        <div className="bg-[#00314F] border border-white/20 rounded-2xl shadow-2xl w-[95vw] max-w-6xl max-h-[90vh] overflow-hidden flex flex-col mt-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#00314F]">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              🔍 Duplicate Review
              <span className="text-sm font-normal text-white/60 ml-2">
                {allRecords.length} potential matches found
              </span>
            </h2>
            {reason && (
              <p className="text-sm text-white/50 mt-1">
                Detection reason: {reason}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl leading-none px-2"
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-6 py-3 bg-white/5 border-b border-white/10">
          <div className="flex items-center gap-4 text-sm">
            <span className={`px-3 py-1 rounded-full ${step === 'select' ? 'bg-[#0DB14B] text-white' : 'bg-white/10 text-white/50'}`}>
              1. Select Main Record
            </span>
            <span className="text-white/30">→</span>
            <span className={`px-3 py-1 rounded-full ${step === 'confirm' ? 'bg-[#0DB14B] text-white' : 'bg-white/10 text-white/50'}`}>
              2. Confirm Duplicates
            </span>
            <span className="text-white/30">→</span>
            <span className={`px-3 py-1 rounded-full ${step === 'done' ? 'bg-[#0DB14B] text-white' : 'bg-white/10 text-white/50'}`}>
              3. Done!
            </span>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="px-6 py-3 bg-red-500/20 border-b border-red-500/30 text-red-200 text-sm">
            ⚠️ {error}
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 overflow-auto p-6">
          {step === 'select' && (
            <div>
              <p className="text-white/80 mb-4">
                <strong>Step 1:</strong> Compare these records side-by-side. Click "KEEP THIS" on the one you want to mark as the <strong>main record</strong> (canonical).
                The others will be marked as duplicates.
              </p>
              
              {/* Cards grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {remainingRecords.map((record) => {
                  const recordId = record._id || record.id;
                  const isSelected = selectedCanonical === recordId;
                  
                  return (
                    <div
                      key={recordId}
                      className={`relative bg-white/5 border-2 rounded-xl overflow-hidden transition-all ${
                        isSelected 
                          ? 'border-[#0DB14B] ring-2 ring-[#0DB14B]/50' 
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      {/* Selected badge */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-[#0DB14B] text-white text-xs font-bold px-2 py-1 rounded-full">
                          ✓ MAIN
                        </div>
                      )}

                      {/* Image */}
                      <div 
                        className="h-40 bg-black/30 flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => {
                          const imgSrc = record.thumbnail_url || record.image_url || `${backendUrl}/api/inspections/${record._id || record.id}/image`;
                          setLightboxImage(imgSrc);
                        }}
                        title="Click to view full size"
                      >
                        {(record.thumbnail_url || record.image_url || record._id || record.id) ? (
                          <img
                            src={record.thumbnail_url || record.image_url || `${backendUrl}/api/inspections/${record._id || record.id}/image`}
                            alt="Tag"
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span className="text-white/30 text-4xl">📷</span>
                        )}
                      </div>

                      {/* Details */}
                      <div className="p-4">
                        <h3 className="text-white font-semibold truncate mb-1">
                          {record.business_name || 'Unknown Business'}
                        </h3>
                        <p className="text-white/60 text-sm truncate">
                          {[
                            record.site_street_address || record.business_address,
                            record.site_city,
                            record.site_state
                          ].filter(Boolean).join(', ') || '—'}
                        </p>
                        
                        <div className="mt-3 space-y-1 text-xs text-white/50">
                          <div className="flex justify-between">
                            <span>Submitted:</span>
                            <span className="text-white/70">{formatDate(record.created_at || record.submitted_at)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>By:</span>
                            <span className="text-white/70 truncate ml-2">{record.submitted_by_email || record.submitted_by || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Status:</span>
                            <span className={`font-semibold ${
                              record.status === 'approved' ? 'text-green-400' :
                              record.status === 'rejected' ? 'text-red-400' :
                              'text-yellow-400'
                            }`}>
                              {record.status || 'pending'}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-4 space-y-2">
                          <button
                            onClick={() => handleSelectCanonical(record)}
                            className={`w-full py-2 rounded-lg font-semibold text-sm transition-colors ${
                              isSelected
                                ? 'bg-[#0DB14B] text-white'
                                : 'bg-white/10 hover:bg-[#0DB14B]/50 text-white/80 hover:text-white'
                            }`}
                          >
                            {isSelected ? '✓ KEEPING THIS ONE' : 'KEEP THIS'}
                          </button>
                          <button
                            onClick={() => handleMarkNotDuplicate(record)}
                            className="w-full py-2 rounded-lg font-semibold text-sm bg-white/5 hover:bg-red-500/20 text-white/50 hover:text-red-200 border border-white/10 hover:border-red-500/30 transition-colors"
                          >
                            ✗ Not a Duplicate
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 'confirm' && (
            <div>
              <p className="text-white/80 mb-4">
                <strong>Step 2:</strong> Review each record. Click "Yes, it's a duplicate" or "No, skip this one".
                <span className="ml-2 text-white/50">
                  ({currentIndex + 1} of {toConfirm.length})
                </span>
              </p>

              {toConfirm[currentIndex] && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Canonical (left) */}
                  <div className="bg-[#0DB14B]/10 border-2 border-[#0DB14B] rounded-xl p-4">
                    <div className="text-[#0DB14B] font-bold text-sm mb-3">
                      ✓ MAIN RECORD (Keeping this one)
                    </div>
                    {renderComparisonCard(remainingRecords.find(r => (r._id || r.id) === selectedCanonical))}
                  </div>

                  {/* Current candidate (right) */}
                  <div className="bg-yellow-500/10 border-2 border-yellow-500/50 rounded-xl p-4">
                    <div className="text-yellow-400 font-bold text-sm mb-3">
                      ❓ IS THIS A DUPLICATE?
                    </div>
                    {renderComparisonCard(toConfirm[currentIndex])}
                    
                    <div className="mt-4 flex gap-3">
                      <button
                        onClick={handleConfirmOne}
                        disabled={isSubmitting}
                        className="flex-1 py-3 rounded-lg font-bold text-white bg-[#0DB14B] hover:bg-[#0DB14B]/80 transition-colors disabled:opacity-50"
                      >
                        ✓ Yes, Duplicate
                      </button>
                      <button
                        onClick={handleSkipOne}
                        disabled={isSubmitting}
                        className="flex-1 py-3 rounded-lg font-bold text-white bg-white/10 hover:bg-white/20 border border-white/20 transition-colors disabled:opacity-50"
                      >
                        ✗ No, Skip
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">✅</div>
              <h3 className="text-2xl font-bold text-white mb-2">Duplicates Processed!</h3>
              <p className="text-white/60 mb-6">
                {confirmedDuplicates.size} record{confirmedDuplicates.size !== 1 ? 's' : ''} marked as duplicate{confirmedDuplicates.size !== 1 ? 's' : ''}.
                {notDuplicates.size > 0 && ` ${notDuplicates.size} marked as not-duplicate.`}
              </p>
              <button
                onClick={onClose}
                className="px-8 py-3 rounded-xl font-bold text-white bg-[#0DB14B] hover:bg-[#0DB14B]/80 transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'select' && (
          <div className="px-6 py-4 border-t border-white/10 bg-white/5 flex items-center justify-between">
            <div className="text-white/50 text-sm">
              Selected: <strong className="text-white">{remainingRecords.find(r => (r._id || r.id) === selectedCanonical)?.business_name || '—'}</strong>
              <span className="mx-2">•</span>
              {toConfirm.length} duplicate{toConfirm.length !== 1 ? 's' : ''} to confirm
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleConfirmAll}
                disabled={toConfirm.length === 0 || isSubmitting}
                className="px-6 py-2 rounded-lg font-semibold text-white bg-[#235EAC] hover:bg-[#235EAC]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⚙️</span>
                    <span>{submitProgress || 'Processing...'}</span>
                    {jobElapsedTime > 0 && <span className="text-xs opacity-70">({jobElapsedTime}s)</span>}
                  </span>
                ) : (
                  `Confirm All ${toConfirm.length} as Duplicates`
                )}
              </button>
              <button
                onClick={handleStartConfirmation}
                disabled={toConfirm.length === 0}
                className="px-6 py-2 rounded-lg font-semibold text-white bg-[#0DB14B] hover:bg-[#0DB14B]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Review One-by-One →
              </button>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Image Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-[100001] flex items-center justify-center p-4 pt-20 pb-6 bg-black/90 backdrop-blur-md"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white text-4xl leading-none px-3 py-1 rounded-lg hover:bg-white/10 transition-colors z-10"
            title="Close (or click anywhere)"
          >
            ×
          </button>
          <div className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center">
            <img
              src={lightboxImage}
              alt="Full size preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white/60 text-sm bg-black/50 px-4 py-2 rounded-lg">
            Click anywhere to close
          </div>
        </div>
      )}
    </>,
    document.body
  );

  function renderComparisonCard(record) {
    if (!record) return null;
    return (
      <div className="flex gap-4">
        <div 
          className="w-32 h-32 bg-black/30 rounded-lg flex-shrink-0 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => {
            const imgSrc = record.thumbnail_url || record.image_url || `${backendUrl}/api/inspections/${record._id || record.id}/image`;
            setLightboxImage(imgSrc);
          }}
          title="Click to view full size"
        >
          {(record.thumbnail_url || record.image_url || record._id || record.id) ? (
            <img
              src={record.thumbnail_url || record.image_url || `${backendUrl}/api/inspections/${record._id || record.id}/image`}
              alt="Tag"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/30 text-3xl">📷</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-bold truncate">{record.business_name || 'Unknown'}</h4>
          <p className="text-white/60 text-sm truncate">
            {[record.site_street_address || record.business_address, record.site_city, record.site_state].filter(Boolean).join(', ')}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div className="text-white/50">Submitted:</div>
            <div className="text-white/80">{formatDate(record.created_at)}</div>
            <div className="text-white/50">By:</div>
            <div className="text-white/80 truncate">{record.submitted_by_email || record.submitted_by || '—'}</div>
            <div className="text-white/50">Status:</div>
            <div className={`font-semibold ${record.status === 'approved' ? 'text-green-400' : record.status === 'rejected' ? 'text-red-400' : 'text-yellow-400'}`}>
              {record.status || 'pending'}
            </div>
            <div className="text-white/50">ID:</div>
            <div className="text-white/60 text-[10px] font-mono truncate">{record._id || record.id}</div>
          </div>
        </div>
      </div>
    );
  }
}

export default DuplicateReviewModal;

